import CssBaseline from '@material-ui/core/CssBaseline'
import { MuiThemeProvider, Theme, makeStyles } from '@material-ui/core/styles'
import { Loader, LoaderOptions } from 'google-maps'
import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'
import 'moment/locale/es'

import { tokenLogin } from 'api'
import { snackbar, SnackbarContainer } from 'components'
import { UserContext } from 'contexts'
import { Admin } from 'features/Main/Admins/types'
import { useApiCall } from 'hooks'
import { TokenLoginResponse } from 'types'
import { createBackofficeTheme } from 'styles'
import { setAuthHeader, removeAuthHeader } from 'utils'

import { Login } from './Login'
import { Main } from './Main'
import { ConfirmRegistration } from './ConfirmRegistration'
import { Colors, GetStyleResponse } from './Main/Styles/types'
import { getStyles } from './Main/Styles/api'
import { Terms } from './Terms'

moment.locale('es')

const useGlobalStyles = makeStyles(() => ({
  '@global': {
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#bbb',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#aaa',
    },
  },
}))

const loadGoogleMaps = async () => {
  const options: LoaderOptions = { libraries: ['places'] }
  const loader = new Loader(process.env.REACT_APP_GOOGLE_MAPS_API_KEY, options)
  return loader.load()
}

const App: React.FC = () => {
  useGlobalStyles()
  const [isLoading, setIsLoading] = useState(true)
  const [isThemeLoading, setIsThemeLoading] = useState(true)
  const [admin, setAdmin] = useState<Admin | undefined>(() => {
    const localAdmin = localStorage.getItem('admin')
    if (!localAdmin) return undefined
    return JSON.parse(localAdmin)
  })

  const [tokenLoginApi] = useApiCall<void, TokenLoginResponse>(tokenLogin)

  useEffect(() => {
    document.title = 'Punta Carretas | Backoffice'
    const favicon = '/favicon-pcarretas.ico'
    const faviconLink: HTMLLinkElement = document.querySelector("link[rel*='icon']")!
    faviconLink.href += favicon
    document.getElementsByTagName('head')[0].appendChild(faviconLink)
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setIsLoading(false)
      return
    }

    const updateAdmin = async () => {
      try {
        setAuthHeader(token)
        const { user: updatedAdmin } = await tokenLoginApi()
        setAdmin(updatedAdmin)
        localStorage.setItem('admin', JSON.stringify(updatedAdmin))
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      } catch (error) {
        removeAuthHeader()
        setAdmin(undefined)
        localStorage.clear()
      } finally {
        setIsLoading(false)
      }
    }
    updateAdmin()
  }, [tokenLoginApi])

  useEffect(() => {
    loadGoogleMaps()
  }, [])

  const [theme, setTheme] = useState<Theme>(createBackofficeTheme())
  const [styleApi] = useApiCall<void, GetStyleResponse>(getStyles)

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await styleApi()
        const { style } = response
        const colors: Colors = JSON.parse(style.colors.toString())
        const newTheme = createBackofficeTheme({
          secondaryMain: colors.SECONDARY_MAIN,
          background: colors.BACKGROUND,
          primaryContrast: colors.PRIMARY_CONTRAST,
        })
        setTheme(newTheme)
        setIsThemeLoading(false)
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      } catch (error) {
        console.error('Error fetching theme data:', error)
        setTheme(createBackofficeTheme())
        setIsThemeLoading(false)
      }
    }

    fetchTheme()
  }, [styleApi])

  return (
    <>
      {!isThemeLoading ? (
        <MuiThemeProvider theme={theme}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <>
              <CssBaseline />
              {!isLoading && (
                <UserContext.Provider value={{ admin, setAdmin }}>
                  <Router>
                    <Switch>
                      <Route path="/terms" component={Terms} />
                      <Route path="/login" component={Login} />
                      <Route path="/confirm-registration" exact component={ConfirmRegistration} />
                      <Main />
                    </Switch>
                  </Router>
                  <SnackbarContainer
                    ref={ref => {
                      snackbar.containerInstance = ref
                    }}
                  />
                </UserContext.Provider>
              )}
            </>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      ) : null}
    </>
  )
}

export { App }
